import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Plus,
  RefreshCw,
  Building2,
  UserCheck,
  Calendar,
} from "lucide-react";
import Layout from "./Layout";
import { useAuth } from "../context/AuthContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import "./Dashboard.css";

const Dashboard = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Enhanced state management
  const [stats, setStats] = useState({
    totalFirms: 0,
    activeFirms: 0,
    inactiveFirms: 0,
    thisMonth: 0,
  });
  const [recentFirms, setRecentFirms] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load dashboard data with Firebase integration
  const loadDashboardData = useCallback(async () => {
    setDataLoading(true);
    setError(null);

    try {
      const clientsRef = collection(db, "ca_admin");
      const snapshot = await getDocs(clientsRef);

      const clientsArray = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Only include documents that are CA firms (not admin users)
        if (data.firmName) {
          clientsArray.push({
            id: doc.id,
            ...data,
          });
        }
      });

      if (clientsArray.length > 0) {
        // Calculate enhanced statistics
        const totalFirms = clientsArray.length;
        // const activeFirms = clientsArray.filter(
        //   (client) => client.status === "active"
        // ).length;
        // const inactiveFirms = totalFirms - activeFirms;

        // Get firms added this month
        const thisMonth = new Date().getMonth();
        const thisYear = new Date().getFullYear();
        const thisMonthFirms = clientsArray.filter((client) => {
          const createdDate = new Date(client.createdAt);
          return (
            createdDate.getMonth() === thisMonth &&
            createdDate.getFullYear() === thisYear
          );
        }).length;

        setStats({
          totalFirms,
          // activeFirms,
          // inactiveFirms,
          thisMonth: thisMonthFirms,
        });

        // Get recent firms (last 4)
        const sortedFirms = clientsArray
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 4);
        setRecentFirms(sortedFirms);
      } else {
        // Fallback to localStorage
        const localClients = JSON.parse(
          localStorage.getItem("caClients") || "[]"
        );
        setStats({
          totalFirms: localClients.length,
          // activeFirms: localClients.filter((c) => c.status === "active").length,
          // inactiveFirms: localClients.filter((c) => c.status !== "active")
          //   .length,
          thisMonth: 0,
        });
        setRecentFirms(localClients.slice(0, 4));
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error loading dashboard data:", error);

      // Fallback to localStorage on error
      try {
        const localClients = JSON.parse(
          localStorage.getItem("caClients") || "[]"
        );
        setStats({
          totalFirms: localClients.length,
          activeFirms: localClients.filter((c) => c.status === "active").length,
          inactiveFirms: localClients.filter((c) => c.status !== "active")
            .length,
          thisMonth: 0,
        });
        setRecentFirms(localClients.slice(0, 4));
        setError("Using offline data. Click refresh to sync with server.");
      } catch (localError) {
        setError("Failed to load dashboard data.");
        setStats({
          totalFirms: 0,
          activeFirms: 0,
          inactiveFirms: 0,
          thisMonth: 0,
        });
      }
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated, loadDashboardData]);

  const handleRefresh = () => {
    loadDashboardData();
  };

  if (authLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Single stat card configuration
  const statCard = {
    title: "Total CA Firms",
    value: stats.totalFirms,
    icon: Building2,
    color: "primary",
    href: "/clients",
  };

  return (
    <Layout>
      {/* Header matching ClientManagement style */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h2 fw-bold mb-0">Dashboard Overview</h1>
          <p className="text-muted mb-0">
            Welcome back, Super Admin!
            {lastUpdated && (
              <span className="ms-2 small">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        {/* Buttons removed as per user request */}
      </div>

      {/* Error Alert */}
      {error && (
        <div
          className="alert alert-warning alert-dismissible fade show mb-4"
          role="alert"
        >
          <strong>Notice:</strong> {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Stat Card and Quick Actions on Same Line */}
      <div className="row g-4 mb-4">
        {/* Single Stat Card */}
        <div className="col-lg-4">
          <div
            onClick={() => navigate(statCard.href)}
            className="card h-100 shadow-sm"
            style={{ cursor: "pointer" }}
          >
            <div className="card-body h-100 d-flex flex-column">
              <div className="mb-3">
                <div className={`stat-icon bg-${statCard.color}`}>
                  <statCard.icon size={24} />
                </div>
              </div>
              <div className="stat-content flex-grow-1 d-flex flex-column justify-content-center">
                {dataLoading ? (
                  <div className="stat-skeleton">
                    <div className="skeleton-line mb-2"></div>
                    <div className="skeleton-line small"></div>
                  </div>
                ) : (
                  <>
                    <h3 className="stat-value">{statCard.value}</h3>
                    <p className="stat-label">{statCard.title}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header">
              <h5 className="card-title mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div
                    className="action-card"
                    onClick={() => navigate("/clients")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="action-icon bg-primary">
                      <Users size={20} />
                    </div>
                    <div className="action-content">
                      <h6 className="action-title">Manage CA Firms</h6>
                      <p className="action-description">
                        Add, edit, or view CA firm details and credentials
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    className="action-card"
                    onClick={() => navigate("/clients")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="action-icon bg-success">
                      <Plus size={20} />
                    </div>
                    <div className="action-content">
                      <h6 className="action-title">Add New Firm</h6>
                      <p className="action-description">
                        Register a new CA firm and create login credentials
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Firms - Full Width with 2 Columns */}
      <div className="row g-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Recent Firms</h5>
              {/* <button className="btn btn-sm btn-outline-primary" onClick={() => navigate("/clients")}>
                View All
              </button> */}
            </div>
            <div className="card-body">
              {dataLoading ? (
                <div className="row g-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="col-md-6">
                      <div className="d-flex align-items-center p-3 border rounded">
                        <div className="skeleton-circle me-3"></div>
                        <div className="flex-grow-1">
                          <div className="skeleton-line mb-1"></div>
                          <div className="skeleton-line small"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentFirms.length > 0 ? (
                <div className="row g-3">
                  {recentFirms.map((firm, index) => (
                    <div key={index} className="col-md-6">
                      <div className="recent-item-card">
                        <div className="recent-icon">
                          <UserCheck size={16} />
                        </div>
                        <div className="recent-content">
                          <h6 className="recent-title">{firm.firmName}</h6>
                          <p className="recent-subtitle">
                            {firm.contactPerson}
                          </p>
                          <div className="recent-meta">
                            <Calendar size={12} className="me-1" />
                            {new Date(firm.createdAt).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </div>
                        </div>
                        <div className="recent-status">
                          <span
                            className={`status-badge ${firm.status === "active"
                              ? "status-active"
                              : "status-inactive"
                              }`}
                          >
                            {firm.status || "active"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <Building2 size={48} className="empty-icon" />
                  <p className="empty-text">No firms added yet</p>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => navigate("/clients")}
                  >
                    Add First Firm
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout >
  );
};

export default Dashboard;
